import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ArrowRight, X } from 'lucide-react';
import { apiClient } from '../../services/api/apiClient';
import { getImageUrl } from '../../utils/imageUtils';
import { useLenis } from '../../hooks/useLenis';
import { useAuth } from '../../contexts/AuthContext';

// Channel-Name muss exakt mit dem Listener im CheckoutPageTW übereinstimmen.
const VERIFY_CHANNEL = 'rf-email-verified';

export const EmailVerificationPageTW: React.FC = () => {
  useLenis();

  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');
  const verifyTriggered = useRef(false); // StrictMode-Schutz: kein doppelter API-Call
  const { isAuthenticated } = useAuth();

  const token = searchParams.get('token');
  // continue-Parameter aus dem Mail-Link: gibt an, dass der User aus dem
  // Kombi-Checkout-Wizard kam. Wenn vorhanden → BroadcastChannel-Event +
  // Auto-Close, damit der erste Tab den State C übernehmen kann.
  const continueParam = searchParams.get('continue');
  const isFromCheckout = continueParam?.startsWith('/checkout') === true;
  const logoUrl = getImageUrl('/images/branding/renditefuchs-logo.svg');

  // Verifikation triggern (nur einmal, auch im StrictMode)
  useEffect(() => {
    if (verifyTriggered.current) return;
    verifyTriggered.current = true;

    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Ungültiger oder fehlender Verifikationstoken.');
        return;
      }
      try {
        const response = await apiClient.verifyEmail(token);
        if (response.success) {
          setStatus('success');
          setMessage(response.message || 'E-Mail-Adresse wurde erfolgreich bestätigt!');

          // Wenn der User aus dem Checkout-Wizard kam: dem ersten Tab Bescheid
          // sagen, damit er von State B auf State C wechseln kann.
          // Zwei parallele Mechanismen für maximale Zuverlässigkeit:
          //   1. BroadcastChannel (modern, sofortig, aber flüchtig)
          //   2. localStorage-Event (älter, garantiert Tab-übergreifend, hält
          //      auch nach Reload des ersten Tabs)
          if (isFromCheckout) {
            const payload = JSON.stringify({
              type: 'verified',
              continueUrl: continueParam,
              ts: Date.now(),
            });
            if (typeof BroadcastChannel !== 'undefined') {
              try {
                const ch = new BroadcastChannel(VERIFY_CHANNEL);
                ch.postMessage(JSON.parse(payload));
                ch.close();
              } catch {}
            }
            try {
              // storage-Event triggert nur, wenn der WERT sich ändert — daher
              // ts: Date.now() im Payload, damit wiederholte Verify-Klicks
              // jeweils ein frisches Event auslösen.
              localStorage.setItem('rf-email-verified', payload);
            } catch {}
          }
        } else {
          setStatus('error');
          setMessage(response.error || 'Fehler bei der E-Mail-Bestätigung.');
        }
      } catch (error: any) {
        setStatus('error');
        setMessage(
          error?.response?.data?.message ||
          error?.message ||
          'Fehler bei der E-Mail-Bestätigung. Bitte versuchen Sie es erneut.'
        );
      }
    };

    verifyEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hinweis: window.close() funktioniert NUR für Tabs, die per JavaScript
  // (window.open) geöffnet wurden. Tabs, die der User selbst aus einem Mail-
  // Link öffnet, kann der Browser aus Sicherheitsgründen NICHT automatisch
  // schließen. Deshalb verzichten wir auf einen Countdown und zeigen einen
  // klaren manuellen Hinweis + Schließen-Button (der bei Tab-per-Mail-Link
  // ebenfalls fehlschlägt, aber wir versuchen es).

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-sm space-y-10">
        {/* Logo & Header */}
        <div>
          <Link to="/" aria-label="Zur Startseite" className="block">
            <img alt="RENDITEFUCHS" src={logoUrl} className="mx-auto h-10 w-auto" />
          </Link>
          <h2 className="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white">
            {status === 'loading' && 'E-Mail wird bestätigt...'}
            {status === 'success' && 'Bestätigung erfolgreich'}
            {status === 'error' && 'Bestätigung fehlgeschlagen'}
          </h2>
        </div>

        <div className="space-y-6">
          {/* Loading */}
          {status === 'loading' && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-10 w-10 text-brand animate-spin mb-4" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Bitte warten Sie, während wir Ihre E-Mail-Adresse bestätigen.
              </p>
            </div>
          )}

          {/* Success */}
          {status === 'success' && (
            <>
              <div className="p-4 rounded-lg bg-status-success/10 border border-status-success/50">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-status-success shrink-0 mt-0.5" />
                  <p className="text-sm text-status-success">{message}</p>
                </div>
              </div>

              {isFromCheckout && continueParam ? (
                // User kam aus dem Kombi-Checkout: zwei Wege möglich
                //   1. Anderer Tab ist offen → User klickt "Tab schließen"
                //   2. Nur dieser Tab → User klickt "Zur Zahlung" (primärer Weg)
                // Wir präsentieren beide Optionen klar, damit der User nicht
                // erraten muss, was er machen soll.
                <div className="space-y-4">
                  <Link
                    to={continueParam}
                    className="flex w-full justify-center items-center gap-2 rounded-md bg-brand px-3 py-2.5 text-sm/6 font-semibold text-gray-900 hover:bg-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand transition-colors no-underline"
                  >
                    <span>Weiter zur Zahlung</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  <div className="text-center pt-2 border-t border-gray-200 dark:border-white/10">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 mb-2">
                      Falls du im anderen Tab bleiben willst:
                    </p>
                    <button
                      type="button"
                      onClick={() => { try { window.close(); } catch {} }}
                      className="inline-flex items-center gap-2 rounded-md bg-gray-100 dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      <X className="h-4 w-4" />
                      Diesen Tab schließen
                    </button>
                  </div>
                </div>
              ) : (
                // Klassischer Flow (Bestands-User, Re-Verify, Email-Change):
                // Wenn der User bereits eingeloggt ist (z.B. Email-Change im
                // Profil), direkt aufs Dashboard. Sonst zum Login.
                <Link
                  to={isAuthenticated ? '/dashboard' : '/login'}
                  className="flex w-full justify-center items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm/6 font-semibold text-gray-900 hover:bg-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand transition-colors no-underline"
                >
                  <span>{isAuthenticated ? 'Zum Dashboard' : 'Weiter zum Login'}</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              )}
            </>
          )}

          {/* Error */}
          {status === 'error' && (
            <>
              <div className="p-4 rounded-lg bg-status-error/10 border border-status-error/50">
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-status-error shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-status-error">{message}</p>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Falls das Problem weiterhin besteht, kontaktieren Sie bitte den Support oder fordern Sie eine neue Bestätigungs-E-Mail an.
                    </p>
                  </div>
                </div>
              </div>

              <Link
                to={isFromCheckout && continueParam ? continueParam : '/'}
                className="flex w-full justify-center items-center gap-2 rounded-md bg-gray-100 dark:bg-gray-800 px-3 py-2 text-sm/6 font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors no-underline"
              >
                {isFromCheckout ? 'Zurück zum Checkout' : 'Zur Startseite'}
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
